import { Component, effect, inject, input, OnDestroy } from '@angular/core';
import { ClientRequestIdentityHelper, CommonClientRequestsState } from '@aiwyn/common/client-requests';
import { ClientRequestsModule, SetSelectedClientRequestId } from '@aiwyn/client-requests';
import { Store } from '@ngxs/store';
import {
  OnSecureLinkEntityStatusUpdatedFn,
  SecureLinkClientRequest,
  SecureLinkDisplayAdaptor,
  SecureLinkEntityIdentity,
  SecureLinkEntityStatus,
} from '../../models';
import { SecureLinkEntityType } from '@aiwyn/auth';
import { ClientRequestItemStatus, ClientRequestStatus } from '@aiwyn/api';
import { toObservable } from '@angular/core/rxjs-interop';
import { merge, Subject, switchMap } from 'rxjs';
import { distinctUntilChanged, map, skip, take, takeUntil, tap } from 'rxjs/operators';
import { getLoadedResults, Logger } from '@aiwyn/shared';

@Component({
  selector: 'app-client-request-wrapper',
  standalone: true,
  imports: [
    ClientRequestsModule,
  ],
  templateUrl: './client-request-wrapper.component.html',
  styleUrl: './client-request-wrapper.component.scss'
})
export class ClientRequestWrapperComponent implements SecureLinkDisplayAdaptor<SecureLinkEntityType.CLIENT_REQUEST>, OnDestroy {
  private readonly store = inject(Store);
  private readonly logger = inject(Logger);

  entity = input<SecureLinkClientRequest>(undefined);
  onStatusUpdated = input<OnSecureLinkEntityStatusUpdatedFn>(undefined);
  canShowCompletionDialog = input<boolean>(undefined);
  onSubmissionCompleted$ = new Subject<void>();

  private readonly _unsubscribe$ = new Subject<void>();

  entity$ = toObservable(this.entity);
  status$ = this.entity$.pipe(
    switchMap(clientRequest => merge(
      clientRequest.request.status$,
      this.onSubmissionCompleted$.pipe(
        switchMap(() => getLoadedResults(
          this.store.select(
            CommonClientRequestsState.getClientRequestDetailsLoadable(
              ClientRequestIdentityHelper.parseRequestIdentity(clientRequest.request.identity).id,
            ),
          ),
          true,
        ).pipe(
          take(1),
        )),
        map(details => {
          const doneStatuses = [ClientRequestItemStatus.IN_REVIEW, ClientRequestItemStatus.DONE];
          return details.sections.flatMap(section => section.items).every(item => doneStatuses.includes(item.status))
            ? ClientRequestStatus.DONE
            : ClientRequestStatus.IN_PROGRESS;
        }),
        tap((status) =>
          this.logger.debug(`ClientRequestWrapperComponent: Detected submission completed event, re-evaluated status = ${status}`),
        ),
      ),
    )),
  );

  onStatusUpdated$ = this.status$.pipe(
    skip(1),
    distinctUntilChanged(),
    tap(status => {
      const onStatusUpdatedFn = this.onStatusUpdated();
      if (!onStatusUpdatedFn) {
        this.logger.warn('ClientRequestWrapperComponent: onStatusUpdated callback is not provided at time of status update.');
        return;
      }

      const entityIdentity = ClientRequestWrapperComponent.getEntityIdentity(this.entity());
      const normalizedStatus = ClientRequestWrapperComponent.normalizeStatus(status);
      onStatusUpdatedFn(entityIdentity, normalizedStatus);
    }),
    takeUntil(this._unsubscribe$),
  );

  constructor() {
    effect(() => {
      const clientRequest = this.entity();
      this.store.dispatch(new SetSelectedClientRequestId(
        ClientRequestIdentityHelper.parseRequestIdentity(clientRequest.request.identity).id,
        clientRequest.clientRequestItemIds || [],
      ));
    });

    this.onStatusUpdated$.subscribe();
  }

  static normalizeStatus(status: ClientRequestStatus): SecureLinkEntityStatus {
    return [ClientRequestStatus.IN_REVIEW, ClientRequestStatus.DONE].includes(status)
      ? SecureLinkEntityStatus.COMPLETED
      : SecureLinkEntityStatus.IN_PROGRESS;
  }

  static getEntityIdentity(entity: SecureLinkClientRequest): SecureLinkEntityIdentity<SecureLinkEntityType.CLIENT_REQUEST> {
    return `${SecureLinkEntityType.CLIENT_REQUEST}-${ClientRequestIdentityHelper.parseRequestIdentity(entity.request.identity).id}`;
  }

  ngOnDestroy(): void {
    this._unsubscribe$.next();
    this._unsubscribe$.complete();
  }
}
